import { string } from 'zod';

import { intl } from '@ocx-app/lib/translation/TranslationProvider';

import {
  AllureCashbackAccountQuery,
  CashoutAccountIdentifierInput,
  CashoutAccountType,
  LinkCashbackAccountInput,
} from '@ocx/graphql';
import { validatePhoneNumber } from '@ocx/utils-phone-number';

import { allureCashbackMessages } from './allure-cashback.messages';

export type AllureCashbackCashout = NonNullable<AllureCashbackAccountQuery['cashbackAccount']>['cashout'];

export type AllureCashbackCashoutTypename = AllureCashbackCashout['__typename'];

export const getCashoutProviderLabelMessage = (cashout: AllureCashbackCashout) => {
  return cashout.__typename === 'CashoutAccountPaypal'
    ? allureCashbackMessages.redeemPaypalIdLabel
    : allureCashbackMessages.redeemVenmoIdLabel;
};

export const getCashoutIdentifierDisplayValue = (cashout: AllureCashbackCashout) => {
  const { identifier } = cashout;

  return identifier.__typename === 'CashoutAccountIdentifierEmail' ? identifier.email : identifier.phoneNumber;
};

export type AllureCashbackSetupFormValues = {
  paypalEmail: string;
  venmoEmail: string;
};

export const validateCashoutIdentifier = (value?: string): string | undefined => {
  const trimmed = value?.trim() ?? '';

  if (!trimmed) {
    return undefined;
  }

  const isEmail = string().email().safeParse(trimmed).success;
  const isPhone = validatePhoneNumber(trimmed);
  return isEmail || isPhone ? undefined : intl.formatMessage(allureCashbackMessages.setupCashoutIdentifierInvalid);
};

const mapCashoutIdentifierStringToInput = (value: string): CashoutAccountIdentifierInput => {
  const isEmail = string().email().safeParse(value).success;

  return isEmail ? { email: value } : { phoneNumber: value.replace(/\D/g, '') };
};

export const mapSetupFormValuesToLinkCashbackAccountInput = (
  values: AllureCashbackSetupFormValues,
): LinkCashbackAccountInput => {
  const paypalValue = values.paypalEmail?.trim();
  const venmoValue = values.venmoEmail?.trim();

  if (paypalValue) {
    return {
      type: CashoutAccountType.Paypal,
      identifier: mapCashoutIdentifierStringToInput(paypalValue),
    };
  }

  return {
    type: CashoutAccountType.Venmo,
    identifier: mapCashoutIdentifierStringToInput(venmoValue ?? ''),
  };
};

export const isSetupFormSubmittable = (values: AllureCashbackSetupFormValues) => {
  return !!values.paypalEmail?.trim() || !!values.venmoEmail?.trim();
};

export const isPaypalFieldDisabled = (
  values: AllureCashbackSetupFormValues,
  focusedCashout: AllureCashbackCashoutTypename | null,
) => {
  return !!values.venmoEmail?.trim() || focusedCashout === 'CashoutAccountVenmo';
};

export const isVenmoFieldDisabled = (
  values: AllureCashbackSetupFormValues,
  focusedCashout: AllureCashbackCashoutTypename | null,
) => {
  return !!values.paypalEmail?.trim() || focusedCashout === 'CashoutAccountPaypal';
};
